import type { ClientExperimentVariantAssignment } from '@/services/experiment';
import {
  buildClientExperimentRequestConfig,
  getClientExperimentVariant,
} from '@/services/experiment';
import { useUserStore } from '@/stores/user';

export const PRICING_MULTI_YEAR_PAGE_EXPERIMENT_KEY = 'price_multiyear';
export const PRICING_MULTI_YEAR_PAGE_CONTROL_VARIANT = 'control';
export const PRICING_MULTI_YEAR_PAGE_TREATMENT_VARIANT = 'treatment';

export type PricingMultiYearPageVariant =
  | typeof PRICING_MULTI_YEAR_PAGE_CONTROL_VARIANT
  | typeof PRICING_MULTI_YEAR_PAGE_TREATMENT_VARIANT;

export interface PricingMultiYearPageExperience {
  planSelectionMode: 'multi-year' | 'direct-checkout';
}

interface PricingMultiYearPageAssignment {
  identity: string;
  variantCode: PricingMultiYearPageVariant;
  exposable: boolean;
}

export type PricingMultiYearPageDecision =
  | {
      source: 'assignment';
      identity: string;
      variantCode: PricingMultiYearPageVariant;
      exposable: boolean;
      experience: PricingMultiYearPageExperience;
    }
  | {
      source: 'local-unavailable';
      exposable: false;
      experience: PricingMultiYearPageExperience;
    };

const EXPERIENCE_BY_VARIANT: Record<PricingMultiYearPageVariant, PricingMultiYearPageExperience> = {
  [PRICING_MULTI_YEAR_PAGE_CONTROL_VARIANT]: {
    planSelectionMode: 'multi-year',
  },
  [PRICING_MULTI_YEAR_PAGE_TREATMENT_VARIANT]: {
    planSelectionMode: 'direct-checkout',
  },
};

let activeIdentity: string | null = null;
let assignmentRequest: Promise<PricingMultiYearPageAssignment | null> | null = null;

function getCurrentIdentity(): string | null {
  const { isLogin, userInfo, userSubscription } = useUserStore.getState();
  return isLogin && userInfo?.userId && userSubscription?.exist === false
    ? `user:${userInfo.userId}`
    : null;
}

export function isPricingMultiYearPageVariant(value: unknown): value is PricingMultiYearPageVariant {
  return value === PRICING_MULTI_YEAR_PAGE_CONTROL_VARIANT
    || value === PRICING_MULTI_YEAR_PAGE_TREATMENT_VARIANT;
}

export function adaptPricingMultiYearPageExperience(
  variantCode: PricingMultiYearPageVariant,
): PricingMultiYearPageExperience {
  return EXPERIENCE_BY_VARIANT[variantCode];
}

export function getPricingMultiYearPageUnavailableDecision(): PricingMultiYearPageDecision {
  return {
    source: 'local-unavailable',
    exposable: false,
    experience: adaptPricingMultiYearPageExperience(PRICING_MULTI_YEAR_PAGE_CONTROL_VARIANT),
  };
}

export function resetPricingMultiYearPageExperiment(): void {
  activeIdentity = null;
  assignmentRequest = null;
}

function ensurePricingMultiYearPageAssignment(): Promise<PricingMultiYearPageAssignment | null> {
  const identity = getCurrentIdentity();
  if (!identity) {
    resetPricingMultiYearPageExperiment();
    return Promise.resolve(null);
  }

  if (identity === activeIdentity && assignmentRequest) {
    return assignmentRequest;
  }

  activeIdentity = identity;
  const request = getClientExperimentVariant<ClientExperimentVariantAssignment>(
    PRICING_MULTI_YEAR_PAGE_EXPERIMENT_KEY,
    buildClientExperimentRequestConfig(),
  ).then((response) => {
    const assignment = response.data;
    if (!isPricingMultiYearPageVariant(assignment?.variantCode)) {
      return null;
    }

    return {
      identity,
      variantCode: assignment.variantCode,
      exposable: assignment.exposable === true,
    };
  }).catch(() => {
    if (activeIdentity === identity && assignmentRequest === request) {
      assignmentRequest = null;
    }
    return null;
  });

  assignmentRequest = request;
  return request;
}

export async function initPricingMultiYearPageExperiment(): Promise<void> {
  await ensurePricingMultiYearPageAssignment();
}

export async function resolvePricingMultiYearPageDecision(): Promise<PricingMultiYearPageDecision> {
  const requestedIdentity = getCurrentIdentity();
  if (!requestedIdentity) {
    return getPricingMultiYearPageUnavailableDecision();
  }

  const request = ensurePricingMultiYearPageAssignment();
  const assignment = await request;
  if (
    assignmentRequest !== request
    || activeIdentity !== requestedIdentity
    || getCurrentIdentity() !== requestedIdentity
    || assignment?.identity !== requestedIdentity
  ) {
    return getPricingMultiYearPageUnavailableDecision();
  }

  return {
    source: 'assignment',
    identity: assignment.identity,
    variantCode: assignment.variantCode,
    exposable: assignment.exposable,
    experience: adaptPricingMultiYearPageExperience(assignment.variantCode),
  };
}
